// PlayFirst Parent — WhatsApp connect + reminder frequency card.
// Reused by Settings page and (later) by the post-onboarding upsell flow.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Check, Copy, ExternalLink, Loader2, X } from 'lucide-react';
import { PFP } from './styles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const FREQS = [
  { v: 'daily', l: 'Daily' },
  { v: 'weekly', l: 'Weekly' },
  { v: 'biweekly', l: 'Every 2 weeks' },
  { v: 'monthly', l: 'Monthly' },
  { v: 'never', l: 'Never (pause all)' },
];

export default function WhatsAppConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectResult, setConnectResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parent-app/whatsapp/status`, auth());
      setStatus(res.data);
      setPhone(res.data.phone || '');
    } catch (e) {
      toast.error('Could not load WhatsApp status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const connect = async () => {
    if (!phone.trim()) {
      toast.error('Add a WhatsApp number first');
      return;
    }
    setConnecting(true);
    try {
      const res = await axios.post(
        `${API}/parent-app/whatsapp/connect`,
        { whatsapp_number: phone.trim() },
        auth(),
      );
      setConnectResult(res.data);
      if (!res.data.business_number) {
        toast.error('Business number not configured yet — your code is saved.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start verification');
    } finally {
      setConnecting(false);
    }
  };

  const setFrequency = async (v) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/parent-app/whatsapp/preferences`,
        { reminder_frequency: v },
        auth(),
      );
      setStatus((s) => ({ ...s, reminder_frequency: v, paused: v === 'never' ? true : s?.paused }));
      toast.success('Saved');
    } catch (e) {
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const togglePause = async () => {
    if (!status) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/parent-app/whatsapp/preferences`,
        { reminder_frequency: status.reminder_frequency || 'weekly', paused: !status.paused },
        auth(),
      );
      setStatus((s) => ({ ...s, paused: !s.paused }));
    } catch (e) {
      toast.error('Could not update');
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect WhatsApp? You can reconnect anytime.')) return;
    try {
      await axios.post(`${API}/parent-app/whatsapp/disconnect`, {}, auth());
      toast.success('Disconnected');
      setConnectResult(null);
      refresh();
    } catch (e) {
      toast.error('Could not disconnect');
    }
  };

  if (loading) {
    return (
      <div className={`${PFP.card} p-6 flex items-center justify-center`}>
        <Loader2 className="w-5 h-5 animate-spin text-[#7C6F9C]" />
      </div>
    );
  }

  return (
    <div className={`${PFP.card} p-6`} data-testid="whatsapp-connect-card">
      <div className="flex items-center gap-2 mb-3 text-[#7C6F9C]">
        <MessageCircle className="w-5 h-5" />
        <p className="text-xs uppercase tracking-wider font-medium">WhatsApp companion</p>
      </div>

      {/* Connected & verified */}
      {status?.verified ? (
        <div data-testid="wa-state-verified">
          <div className="flex items-center gap-2 mb-3">
            <Check className="w-4 h-4 text-emerald-600" />
            <span className="text-sm text-stone-700">
              Connected to {status.phone || 'your number'}
            </span>
          </div>

          <p className="text-sm text-stone-600 mb-2">How often should I check in?</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {FREQS.map((f) => (
              <button
                key={f.v}
                type="button"
                onClick={() => setFrequency(f.v)}
                disabled={saving}
                data-testid={`wa-freq-${f.v}`}
                className={`${PFP.chip} ${
                  (status.reminder_frequency || 'weekly') === f.v
                    ? PFP.chipActive
                    : PFP.chipIdle
                }`}
              >
                {f.l}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={togglePause}
              disabled={saving}
              data-testid="wa-toggle-pause-btn"
              className={PFP.btnGhost + ' inline-flex items-center gap-1'}
            >
              {status.paused ? 'Resume messages' : 'Pause messages'}
            </button>
            <button
              type="button"
              onClick={disconnect}
              data-testid="wa-disconnect-btn"
              className="rounded-full px-4 py-2 text-red-600 hover:bg-red-50 text-sm inline-flex items-center gap-1"
            >
              <X className="w-4 h-4" /> Disconnect
            </button>
          </div>
          {status.paused && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-2xl border border-amber-200">
              Outbound messages are paused. You can still send commands like IDEA from your phone.
            </p>
          )}
          {status?.business_number && (
            <p className="mt-3 text-[11px] text-stone-500 leading-snug">
              Messages come from <strong className="font-mono text-stone-700">+{status.business_number.replace(/^\+/, '')}</strong>{' '}
              — save as <em>PlayFirst</em> in your contacts if you haven&apos;t yet.
            </p>
          )}
        </div>
      ) : connectResult ? (
        /* Waiting on verification — show deep link */
        <div data-testid="wa-state-pending">
          <p className="text-sm text-stone-700 mb-3">
            Tap the link below in your phone's browser. WhatsApp will open with a
            prefilled <code className="text-xs bg-stone-100 px-1 rounded">START</code> message —
            just send it and you're connected.
          </p>
          {connectResult.deep_link ? (
            <a
              href={connectResult.deep_link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wa-deep-link"
              className={PFP.btnPrimary + ' inline-flex items-center gap-2 mb-3'}
            >
              <ExternalLink className="w-4 h-4" /> Open WhatsApp
            </a>
          ) : (
            <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-sm text-amber-900 mb-3">
              The business WhatsApp number isn't configured on the backend yet.
              Your verification code is saved — once the admin sets
              <code className="ml-1 bg-white px-1 rounded">WHATSAPP_BUSINESS_NUMBER</code>,
              come back and tap the link.
            </div>
          )}
          <div className="text-xs text-stone-500 mb-3">
            Or send this message manually to{' '}
            <strong>{connectResult.business_number ? `+${connectResult.business_number}` : 'the PlayFirst WhatsApp number'}</strong>:
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 rounded-xl bg-stone-100 text-sm break-all">
              START {connectResult.verification_token}
            </code>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(`START ${connectResult.verification_token}`);
                toast.success('Copied');
              }}
              className="rounded-full px-3 py-2 text-[#7C6F9C] hover:bg-[#7C6F9C]/10"
              data-testid="wa-copy-token-btn"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={refresh}
            className={PFP.btnGhost + ' mt-3'}
            data-testid="wa-recheck-btn"
          >
            I've sent it — check status
          </button>
        </div>
      ) : (
        /* Not connected yet — collect number */
        <div data-testid="wa-state-empty">
          <p className="text-sm text-stone-600 mb-3">
            Get gentle daily ideas and weekly check-ins on WhatsApp. You can pause anytime.
          </p>

          {/* Prominent PlayFirst WhatsApp bot number so parents can save
              it to their contacts BEFORE they hit Connect — makes the
              handshake message + all future pings land from a named
              contact, not an unknown number. */}
          {status?.business_number && (
            <div
              className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3"
              data-testid="wa-bot-number-card"
            >
              <div className="text-xs font-medium text-emerald-900 mb-1">
                Save the PlayFirst WhatsApp bot to your contacts
              </div>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 px-3 py-2 rounded-xl bg-white text-sm font-mono text-stone-800 select-all"
                  data-testid="wa-bot-number"
                >
                  +{status.business_number.replace(/^\+/, '')}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(`+${status.business_number.replace(/^\+/, '')}`);
                    toast.success('Copied — now paste it into your Contacts');
                  }}
                  className="rounded-full px-3 py-2 text-emerald-700 hover:bg-emerald-100"
                  data-testid="wa-bot-number-copy"
                  aria-label="Copy PlayFirst WhatsApp number"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[11px] text-emerald-800/80 mt-1.5 leading-snug">
                Save as <strong>PlayFirst</strong> in your phone contacts, then tap Connect
                below. Your messages will land from a friendly name — not an unknown number.
              </p>
            </div>
          )}

          <label className="block text-sm text-stone-600 mb-1">Your WhatsApp number</label>
          <div className="flex gap-2">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+27 82 555 1234"
              className="flex-1 h-12 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              data-testid="wa-phone-input"
            />
            <button
              type="button"
              onClick={connect}
              disabled={connecting}
              className={PFP.btnPrimary + ' inline-flex items-center gap-2'}
              data-testid="wa-connect-btn"
            >
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
              {connecting ? 'Sending…' : 'Connect'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
